import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gavel, Instagram, Facebook, Twitter, Linkedin, Youtube } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const PRIMARY_SLUGS = ["property", "businesses-for-sale", "cars", "bikes-scooters"];

const ICONS = {
  instagram: { Icon: Instagram, label: "Follow AuctionBulls on Instagram" },
  facebook: { Icon: Facebook, label: "Follow AuctionBulls on Facebook" },
  x: { Icon: Twitter, label: "Follow AuctionBulls on X" },
  linkedin: { Icon: Linkedin, label: "Follow AuctionBulls on LinkedIn" },
  youtube: { Icon: Youtube, label: "Subscribe to AuctionBulls on YouTube" },
};

export default function Footer() {
  const [social, setSocial] = useState({});
  const [primary, setPrimary] = useState([]);

  useEffect(() => {
    api.get("/settings").then(r => setSocial(r.data?.social || {})).catch(() => {});
    api.get("/categories/tree").then(r => {
      setPrimary(r.data.filter(c => PRIMARY_SLUGS.includes(c.slug)));
    }).catch(() => {});
  }, []);

  const enabledSocials = Object.entries(social).filter(([_, v]) => v?.enabled && v?.url);

  return (
    <footer className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 mt-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 grid grid-cols-2 md:grid-cols-6 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-amber-400">
              <Gavel className="h-5 w-5" />
            </span>
            <span className="font-extrabold text-lg tracking-tight">Auction<span className="text-amber-500">Bulls</span></span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xs">
            Trusted auctions. Transparent pricing. Secure transactions. Sell for free — pay only when you sell.
          </p>

          {enabledSocials.length > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">Follow AuctionBulls</h4>
              <div className="flex flex-wrap gap-2" data-testid="footer-social">
                {enabledSocials.map(([key, val]) => {
                  const meta = ICONS[key];
                  if (!meta) return null;
                  const Icon = meta.Icon;
                  return (
                    <a
                      key={key}
                      href={val.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={meta.label}
                      data-testid={`footer-social-${key}`}
                      className="h-9 w-9 grid place-items-center rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div>
          <h4 className="font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">AuctionBulls</h4>
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
            <li><Link to="/how-it-works" className="hover:text-amber-600">About</Link></li>
            <li><Link to="/how-it-works" className="hover:text-amber-600">How It Works</Link></li>
            <li><Link to="/how-it-works#buyers" className="hover:text-amber-600">Buyers</Link></li>
            <li><Link to="/how-it-works#sellers" className="hover:text-amber-600">Sellers</Link></li>
            <li><Link to="/blog" className="hover:text-amber-600">Blog</Link></li>
            <li><Link to="/how-it-works" className="hover:text-amber-600">Contact</Link></li>
            <li><span className="text-slate-400">Terms & Conditions</span></li>
            <li><span className="text-slate-400">Privacy Policy</span></li>
            <li><span className="text-slate-400">Auction Rules</span></li>
            <li><span className="text-slate-400">Buyer Protection</span></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">Categories</h4>
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
            {primary.filter(c => c.primary).map(c => (
              <li key={c.id}><Link to={`/auctions?category_id=${c.id}`} className="hover:text-amber-600">{c.name}</Link></li>
            ))}
            <li><Link to="/auctions" className="hover:text-amber-600 font-semibold">More Categories →</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">Marketplace</h4>
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
            <li><Link to="/auctions" className="hover:text-amber-600">Browse Auctions</Link></li>
            <li><Link to="/auctions?sort=ending_soon" className="hover:text-amber-600">Ending Soon</Link></li>
            <li><Link to="/auctions?featured=1" className="hover:text-amber-600">Featured</Link></li>
            <li><Link to="/sell" className="hover:text-amber-600">Sell an Item</Link></li>
            <li><Link to="/signup" className="hover:text-amber-600">Create Account</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">Get Updates</h4>
          <p className="text-xs text-slate-500 mb-3">Auction highlights, guides and market updates — straight to your inbox.</p>
        <form onSubmit={async (e) => {
            e.preventDefault();
            const email = new FormData(e.currentTarget).get("email");
            if (!email) return;
            try {
              await api.post("/newsletter/subscribe", { email, source: "footer" });
              toast.success("Subscribed!", { description: "We'll email highlights soon." });
              e.currentTarget.reset();
            } catch { toast.error("Could not subscribe"); }
          }} className="flex flex-col gap-2">
            <input name="email" type="email" required placeholder="you@example.com" data-testid="newsletter-email"
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm" />
            <button type="submit" data-testid="newsletter-submit"
              className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">Subscribe</button>
          </form>
        </div>
      </div>
      <div className="border-t border-slate-200 dark:border-slate-800 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} AuctionBulls · Bid. Win. Own.
      </div>
    </footer>
  );
}
